import { Button, Heading, Input, Spacer, Stack, useBreakpointValue } from '@chakra-ui/react';
import { PinBottomIcon, PinTopIcon } from '@radix-ui/react-icons';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { ArchiveSummary, GroupedArchive, LetterDirectionality } from '../api/WritingDesk';
import wrapColorTheme from '../lib/WrapColorTheme';
import { colors, styled } from '../Stitches';
import { Divider, PlaceholderText } from './Desk';
import Emoji from './Emoji';
import { EmptyBox } from './graphics/EmptyBox';
import useExpiringState from './hooks/useExpiringState';
import useOpenLetter from './hooks/useOpenLetter';
import { ScrollFade } from './ScrollFade';

// === LIB =====================================================================
const makeDate = (date: string): Date => new Date(date + 'Z');

const formatDate = (date: string) => format(makeDate(date), 'do LLLL yyyy');
const formatDateShorter = (date: string) => format(makeDate(date), 'do LLL yy');
const formatTitle = (title: string) => (title === '' ? <em>Untitled.</em> : <>{title}</>);

// === STYLED COMPONENTS ======================================================
const Display = styled('div', {
  variants: { hidden: { true: { display: 'none' }, false: { display: 'inherit' } } },
});

const ArchiveLineGrid = styled('div', {
  display: 'grid',
  gridGap: 8,

  variants: {
    grid: {
      mobile: { gridTemplateColumns: '32px 3fr 1fr' },
      wide: { gridTemplateColumns: '7.5em 8fr 1fr' },
    },
  },
  defaultVariants: { grid: 'mobile' },
});

const AuthorCell = styled('div', {
  width: '12rem',
  paddingLeft: '$4',

  fontSize: '$1',

  variants: { small: { true: { fontSize: '$0', width: '6rem' } } },
});

const DateCell = styled('div', {
  paddingRight: '$4',

  fontSize: '$1',
  fontFamily: '$mono',
  textAlign: 'right',

  width: '10rem',

  variants: { small: { true: { width: '6rem', fontSize: '$0' } } },
});

const TitleCell = styled('div', {
  fontSize: '$1',
  px: '$2',
  variants: { small: { true: { fontSize: '$0' } } },
  textAlign: 'left',
});

const Empty = ({ tagline }: { tagline: string }) => (
  <Stack spacing={8} p={12}>
    <EmptyBox />
    <PlaceholderText align="center">{tagline}</PlaceholderText>
  </Stack>
);

const EmptyPlaceholder = () => <Empty tagline="Your archive is currently empty." />;
const NoResultsPlaceholder = () => (
  <Empty tagline="We couldn't find any letters that match your search." />
);

const themeVariants = {
  dark: {
    color: {
      color: 'white',
      background: colors.dark.whiteAlpha50,
      borderColor: colors.dark.whiteAlpha300,
    },
    border: {
      boxShadow: `2px 3px 0 ${colors.ldPurple}`,
    },
  },
  light: {
    color: {
      color: 'black',
      borderColor: colors.dark.darkAlpha700,
    },
    border: {
      boxShadow: `2px 3px 0px ${colors.gray900}`,
    },
  },
};

const ArchiveContainerPrimitive = styled(motion.fieldset, {
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: '$3',
  fontFamily: '$mono',

  transitionProperty: 'color',
  transitionDuration: '150ms',
  transitionTimingFunction: 'ease-in',

  variants: {
    theme: {
      dark: { ...themeVariants.dark.border, ...themeVariants.dark.color },
      light: { ...themeVariants.light.border, ...themeVariants.light.color },
    },
    layout: {
      mobile: { padding: '$4' },
      wide: { padding: '$5' },
    },
  },
});

const ArchiveContainer = wrapColorTheme(ArchiveContainerPrimitive);

const ArchiveTitlePrimitive = styled('legend', {
  marginLeft: '$4',
  paddingBlock: '$1',
  paddingInline: '$4',
  borderWidth: 1,
  borderRadius: 2,
  fontFamily: '$heading',
  fontWeight: 500,
  fontSize: '$3',

  variants: {
    theme: {
      dark: { ...themeVariants.dark.color, borderColor: colors.dark.whiteAlpha400 },
      light: { ...themeVariants.light.color },
    },
  },
});

const ArchiveTitle = wrapColorTheme(ArchiveTitlePrimitive);

const GroupedArchiveRowPrimitive = styled('div', {
  borderRadius: '$3',
  padding: '$3',

  cursor: 'pointer',

  '&:hover': { transform: 'scale(1.01)' },
  '&:active': { transform: 'scale(1)' },

  variants: {
    theme: {
      dark: {
        backgroundColor: colors.dark.whiteAlpha50,
        '&:hover': { backgroundColor: colors.dark.whiteAlpha100, transform: 'scale(1.01)' },
        '&:active': { backgroundColor: colors.dark.whiteAlpha200, transform: 'scale(1)' },
      },
      light: {
        // backgroundColor: colors.gray50,
        '&:hover': { backgroundColor: colors.gray100, transform: 'scale(1.01)' },
        '&:active': { backgroundColor: colors.gray200, transform: 'scale(1)' },
      },
    },
  },
});

const GroupedArchiveRow = wrapColorTheme(GroupedArchiveRowPrimitive);

const ShowMoreContainer = styled(motion.div, { paddingLeft: 8, paddingBlock: 4 });

const SentRecievedIndicator = ({ directionality }: { directionality: LetterDirectionality }) => (
  <Stack direction="row">
    {directionality === 'received' && <PinBottomIcon />}
    {directionality === 'sent' && <PinTopIcon />}

    <Display hidden={{ '@initial': true, '@sm': false }}>
      {directionality === 'received' && <p>Received</p>}
      {directionality === 'sent' && <p>Sent</p>}
    </Display>
  </Stack>
);

// === Functional Components ==================================================
const ArchiveLine = ({ letter }: { letter: ArchiveSummary }) => {
  const { id, title, relevant_date, directionality } = letter;

  const onClick = useOpenLetter(id);

  const small = useBreakpointValue([true, false]);
  const dateFormatter = small ? formatDateShorter : formatDate;

  return (
    <GroupedArchiveRow onClick={onClick}>
      <ArchiveLineGrid grid={{ '@sm': 'wide' }}>
        <AuthorCell small={small}>
          <SentRecievedIndicator directionality={directionality} />
        </AuthorCell>
        <TitleCell small={small}>{formatTitle(title)}</TitleCell>
        <DateCell small={small}>{dateFormatter(relevant_date)}</DateCell>
      </ArchiveLineGrid>
    </GroupedArchiveRow>
  );
};

const MotionStack = motion(Stack);

const motionVariants = {
  container: {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        delayChildren: 0,
        staggerChildren: 0.02,
      },
    },
  },
  item: {
    hidden: { opacity: 0 },
    show: { opacity: 1 },
  },
};

const LetterList = ({ letters, expand }: { letters: ArchiveSummary[]; expand: boolean }) => {
  const threshold = 15;
  const [expanded, setExpanded] = useExpiringState(
    `expand-section-${letters[0].correspondent_id}`,
    false,
    30
  );

  const isTruncated = !expand && !expanded && letters.length > threshold;
  const data = !isTruncated ? letters : letters.slice(0, threshold);

  return (
    <MotionStack variants={motionVariants.container} initial="hidden" animate="show" spacing="none">
      {data.map((l) => (
        <motion.div key={l.id} variants={motionVariants.item}>
          <ArchiveLine letter={l} />
        </motion.div>
      ))}
      {isTruncated && (
        <ShowMoreContainer variants={motionVariants.item}>
          <Button size="sm" onClick={() => setExpanded(!expanded)}>
            Show more
          </Button>
        </ShowMoreContainer>
      )}
    </MotionStack>
  );
};

const GroupedArchiveComponent = ({
  data,
  expand,
}: {
  data: GroupedArchive<ArchiveSummary>;
  expand: boolean;
}) => {
  const names: { [id: string]: string } = data
    .map(([id, letters]) => ({
      [id]: letters[0].correspondent_name,
    }))
    .reduce((a, b) => ({ ...a, ...b }));

  return (
    <Stack spacing="6">
      {data.map(([senderId, letters]) => (
        <ScrollFade direction="bottom" interpolationPoint={0.18} key={senderId}>
          <ArchiveContainer layout={{ '@initial': 'mobile', '@sm': 'wide' }}>
            <ArchiveTitle>{names[senderId]}</ArchiveTitle>
            <LetterList expand={expand} letters={letters} />
          </ArchiveContainer>
        </ScrollFade>
      ))}
    </Stack>
  );
};

const useArchive = (data: GroupedArchive<ArchiveSummary>) => {
  const [filter, setFilter] = useState('');

  const letterMatches = (filter: string) => (letter: ArchiveSummary) =>
    [letter.title, letter.correspondent_name, formatDate(letter.relevant_date)]
      .join('')
      .toLowerCase()
      .includes(filter.toLowerCase());

  const archive = React.useMemo(
    () =>
      data
        .map(([id, letters]) => [id, letters.filter(letterMatches(filter))] as const)
        .filter(([_, letters]) => letters.length !== 0) as GroupedArchive<ArchiveSummary>,
    [data, filter]
  );

  return { filter, setFilter, archive };
};

const Archive = ({ data }: { data: GroupedArchive<ArchiveSummary> }) => {
  const { filter, setFilter, archive } = useArchive(data);

  return (
    <div>
      <Stack direction={'row'} justifyContent="space-between" alignItems="center">
        <Heading size="lg">
          <Emoji symbol="🏛" label="Emoji of an archival building" /> Correspondence
        </Heading>
        <Input
          w={['48', '64']}
          placeholder="Search"
          size="md"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </Stack>
      <Spacer height={2} />
      <Divider />

      <Spacer height={4} />

      {Object.entries(archive).length === 0 ? (
        filter.length !== 0 ? (
          <NoResultsPlaceholder />
        ) : (
          <EmptyPlaceholder />
        )
      ) : (
        <GroupedArchiveComponent expand={filter.length !== 0} data={archive} />
      )}

      <Spacer height={4} />
    </div>
  );
};

export default Archive;
